import React, { memo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Box, Button, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import { RootState } from "../../store/store";
import { removeFromComparator, clearComparator } from "../../store/comparatorSlice";
import { trackEvent } from "../../utils/analytics";

const BACKEND_URL = process.env.REACT_APP_API_URL || '';

const thumb = (url: string) =>
  url && url.includes('booking-manager.com')
    ? `${BACKEND_URL}/api/image/?url=${encodeURIComponent(url)}&w=80&h=56&q=70`
    : url;

const ComparatorBar: React.FC = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const items = useSelector((state: RootState) => state.comparator.items);

  const visible = items.length > 0;

  const handleCompare = () => {
    const ids = items.map((i) => i.yachtId).join(',');
    const dateFrom = items[0]?.dateFrom || '';
    const dateTo = items[0]?.dateTo || '';
    trackEvent('compare_opened', { count: items.length });
    window.open(
      `/compare?ids=${ids}&date_from=${encodeURIComponent(dateFrom)}&date_to=${encodeURIComponent(dateTo)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1300,
        bgcolor: "#1a1a2e",
        color: "#fff",
        px: { xs: 2, md: 4 },
        py: 1.5,
        display: "flex",
        alignItems: "center",
        gap: 2,
        boxShadow: "0 -4px 20px rgba(0,0,0,0.3)",
        // Keep always in DOM so the compositing layer is pre-created — prevents
        // the full-page repaint that happens when a position:fixed element mounts.
        transform: visible ? "translateY(0)" : "translateY(100%)",
        transition: "transform 0.22s cubic-bezier(0.4, 0, 0.2, 1)",
        willChange: "transform",
        pointerEvents: visible ? "auto" : "none",
      }}
    >
      {/* Slots */}
      {!isMobile && (
        <Box sx={{ display: "flex", gap: 1.5, flex: 1, overflowX: "auto" }}>
          {items.map((item) => (
            <Box
              key={item.yachtId}
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                gap: 1,
                bgcolor: "rgba(255,255,255,0.1)",
                borderRadius: "8px",
                px: 1,
                py: 0.5,
                minWidth: 160,
                maxWidth: 200,
              }}
            >
              {item.imageUrl ? (
                <Box
                  component="img"
                  src={thumb(item.imageUrl)}
                  alt={item.model}
                  sx={{ width: 56, height: 40, borderRadius: "4px", objectFit: "cover", flexShrink: 0 }}
                />
              ) : (
                <Box sx={{ width: 56, height: 40, bgcolor: "rgba(255,255,255,0.2)", borderRadius: "4px", flexShrink: 0 }} />
              )}
              <Typography sx={{ fontSize: "12px", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {item.model}
              </Typography>
              <IconButton
                size="small"
                onClick={() => dispatch(removeFromComparator(item.yachtId))}
                sx={{ color: "#fff", p: 0.25 }}
              >
                <CloseIcon sx={{ fontSize: "16px" }} />
              </IconButton>
            </Box>
          ))}
          {Array.from({ length: 4 - items.length }).map((_, i) => (
            <Box
              key={`empty-${i}`}
              sx={{
                width: 160,
                height: 56,
                border: "1px dashed rgba(255,255,255,0.3)",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography sx={{ fontSize: "11px", color: "rgba(255,255,255,0.4)" }}>Add boat</Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Mobile: compact count */}
      {isMobile && (
        <Typography sx={{ flex: 1, fontSize: "14px" }}>
          {items.length} boat{items.length > 1 ? "s" : ""} selected
        </Typography>
      )}

      {/* Actions */}
      <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
        <Button
          variant="text"
          size="small"
          onClick={() => dispatch(clearComparator())}
          sx={{ color: "rgba(255,255,255,0.6)", fontSize: "12px" }}
        >
          Clear all
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={items.length < 2}
          startIcon={<CompareArrowsIcon />}
          onClick={handleCompare}
          sx={{ borderRadius: "20px", fontWeight: "bold" }}
        >
          Compare ({items.length})
        </Button>
      </Box>
    </Box>
  );
};

export default memo(ComparatorBar);
